import React, { useEffect, useLayoutEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Sparkles,
  ShieldCheck,
  Leaf,
  Star,
  Truck,
  CalendarCheck,
  Wand2,
  CheckCircle2,
  Quote,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "../components/ui/carousel";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useSEO } from "../lib/useSEO";
import { useServices } from "../lib/servicesCache";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/72a579c8-478b-4bc4-a64a-4bd179574544/images/6752e9231fbc116ca8a2c900c07c8e6b220559cc225d3d2f6b7c4c08e92442f4.png";
const SERVICE_IMG =
  "https://static.prod-images.emergentagent.com/jobs/72a579c8-478b-4bc4-a64a-4bd179574544/images/64a054ba993e19d758cf775bb814c783a1f50eca34f1a203d2af90f7832ec15e.png";
const GDANSK_IMG =
  "https://images.unsplash.com/photo-1713160838345-6ccc5d442c00?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxnZGFuc2slMjBjaXR5fGVufDB8fHx8MTc3NzM5NTczMnww&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const { t, lang } = useI18n();
  useSEO({ titleKey: "seo.home_title", descriptionKey: "seo.home_desc" });
  const [services, setServices] = useState([]);
  const cachedServices = useServices();
  const navigate = useNavigate();
  const location = useLocation();

  // Reviews + total count fetched from the backend (admin can generate /
  // delete / set baseline so this updates live across all visitors).
  const [reviews, setReviews] = useState([]);
  const [reviewsTotal, setReviewsTotal] = useState(0);
  const [reviewIdx, setReviewIdx] = useState(0);

  useEffect(() => {
    let alive = true;
    api
      .get("/reviews", { params: { limit: 24 } })
      .then((r) => {
        if (!alive) return;
        setReviews(Array.isArray(r.data?.items) ? r.data.items : []);
        setReviewsTotal(Number(r.data?.total_count || 0));
      })
      .catch(() => {
        // Silent fall-through — keep the page usable even if the API blips.
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (cachedServices && cachedServices.length) setServices(cachedServices);
  }, [cachedServices]);

  // Scroll to a hash anchor (e.g. #pricing) BEFORE the browser paints, so
  // cross-page nav from a service detail / portal lands directly on the
  // section with no flash of "top of page → jump to pricing".
  useLayoutEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "auto", block: "start" });
  }, [location.hash]);

  // auto-rotate the hero floating review every 4.5s
  useEffect(() => {
    if (!Array.isArray(reviews) || reviews.length === 0) return;
    const id = setInterval(
      () => setReviewIdx((i) => (i + 1) % reviews.length),
      4500,
    );
    return () => clearInterval(id);
  }, [reviews.length]);

  const heroReview =
    Array.isArray(reviews) && reviews.length > 0 ? reviews[reviewIdx] : null;

  return (
    <div data-testid="landing-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-water-grad">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-24 lg:pt-24 lg:pb-32 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 fade-in-up">
            <span
              className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-sky-700 bg-white/70 border border-white px-3 py-1.5 rounded-full"
              data-testid="hero-eyebrow"
            >
              <Sparkles className="w-3.5 h-3.5" /> {t("hero.eyebrow")}
            </span>
            <h1
              className="font-display mt-5 text-5xl lg:text-6xl xl:text-7xl font-bold leading-[0.95] text-slate-900"
              data-testid="hero-title"
            >
              {t("hero.title_a")}
              <br />
              <span className="bg-gradient-to-br from-sky-500 to-sky-700 bg-clip-text text-transparent">
                {t("hero.title_b")}
              </span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
              {t("hero.subtitle")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button
                className="btn-primary inline-flex items-center gap-2"
                onClick={() => navigate("/book")}
                data-testid="hero-cta-book"
              >
                {t("hero.cta")} <ArrowRight className="w-4 h-4" />
              </button>
              <a
                href="#pricing"
                className="px-5 py-3 rounded-xl border border-slate-300 bg-white/70 text-slate-800 font-semibold hover:bg-white transition"
                data-testid="hero-cta-pricing"
              >
                {t("hero.cta_secondary")}
              </a>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-5 max-w-lg">
              {[
                {
                  v:
                    reviewsTotal >= 1000
                      ? `${(reviewsTotal / 1000).toFixed(reviewsTotal >= 10000 ? 0 : 1)}k+`
                      : `${reviewsTotal || 0}+`,
                  l: t("hero.stat_jobs"),
                },
                { v: "4.9★", l: t("hero.stat_rating") },
                { v: "<2", l: t("hero.stat_response") },
              ].map((s) => (
                <div
                  key={s.l}
                  className="rounded-xl border border-slate-200 bg-white/70 p-4"
                >
                  <div className="font-display text-2xl font-bold text-slate-900">
                    {s.v}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-sky-900/20 border border-white">
              <img
                src={HERO_IMG}
                alt="Water splash"
                className="w-full h-[440px] object-cover"
              />
              <div className="absolute bottom-4 left-4 right-4 glass rounded-2xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 grid place-items-center">
                  <ShieldCheck className="w-5 h-5 text-emerald-700" />
                </div>
                <div data-testid="hero-badge">
                  <div className="font-semibold text-sm text-slate-900">
                    {t("hero_badge.title")}
                  </div>
                  <div className="text-xs text-slate-600">
                    {t("hero_badge.sub")}
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute -top-6 -right-6 hidden lg:block rounded-2xl bg-white shadow-xl border border-slate-200 p-4 w-56 fade-in-up">
              {heroReview && (
                <div
                  key={reviewIdx}
                  className="fade-in-up"
                  data-testid="hero-review-card"
                >
                  <div className="flex items-center gap-1.5 text-amber-500">
                    {Array.from({ length: heroReview.rating || 5 }).map(
                      (_, i) => (
                        <Star key={i} className="w-4 h-4 fill-current" />
                      ),
                    )}
                  </div>
                  <p className="mt-2 text-sm text-slate-700 leading-snug line-clamp-3">
                    "{lang === "pl" ? (heroReview.text_pl || heroReview.text) : (heroReview.text_en || heroReview.text)}"
                  </p>
                  <p className="text-xs text-slate-400 mt-2">
                    — {heroReview.author}, {heroReview.location}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-24" id="services">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-sky-600">
              {t("nav.services")}
            </span>
            <h2 className="font-display mt-3 text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight">
              {t("services.title")}
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              {t("services.subtitle")}
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {services.map((s, idx) => (
              <Link
                key={s.id}
                to={`/services/${s.slug}`}
                className="group relative rounded-3xl overflow-hidden border border-slate-200 bg-white hover:-translate-y-1 transition-transform duration-300 shadow-sm hover:shadow-xl"
                data-testid={`service-card-${s.slug}`}
              >
                <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                  <img
                    src={idx === 0 ? SERVICE_IMG : HERO_IMG}
                    alt={lang === "pl" ? s.name_pl : s.name_en}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-7">
                  <h3 className="font-display text-2xl font-semibold text-slate-900">
                    {lang === "pl" ? s.name_pl : s.name_en}
                  </h3>
                  <p className="mt-2 text-slate-600 line-clamp-2">
                    {lang === "pl" ? s.description_pl : s.description_en}
                  </p>
                  <div className="mt-5 flex items-center justify-between">
                    <div>
                      <span className="text-xs text-slate-500">
                        {t("services.from")}
                      </span>
                      <div className="font-display text-2xl font-bold text-sky-700">
                        {s.base_price.toFixed(0)} {t("common.currency")}
                        <span className="text-sm text-slate-500 font-normal ml-1">
                          / {lang === "pl" ? s.unit_pl : s.unit_en}
                        </span>
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1 text-sky-700 text-sm font-semibold">
                      {t("services.learn")}{" "}
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 bg-gradient-to-b from-white to-sky-50/50" id="how">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="font-display text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight">
              {t("how.title")}
            </h2>
            <p className="mt-4 text-slate-600">{t("how.subtitle")}</p>
          </div>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {[
              { icon: CalendarCheck, t: t("how.s1_t"), d: t("how.s1_d") },
              { icon: Truck, t: t("how.s2_t"), d: t("how.s2_d") },
              { icon: Wand2, t: t("how.s3_t"), d: t("how.s3_d") },
            ].map((step, i) => (
              <div
                key={step.t}
                className="relative rounded-2xl border border-slate-200 bg-white p-7"
                data-testid={`how-step-${i + 1}`}
              >
                <div className="absolute -top-4 left-7 w-9 h-9 rounded-xl bg-sky-600 text-white grid place-items-center font-bold shadow-lg shadow-sky-500/30">
                  {i + 1}
                </div>
                <step.icon className="w-7 h-7 text-sky-600" />
                <h3 className="font-display mt-4 text-xl font-semibold">
                  {step.t}
                </h3>
                <p className="mt-2 text-slate-600 text-sm leading-relaxed">
                  {step.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS CAROUSEL */}
      <section className="py-24" id="reviews">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-sky-600">
              {t("reviews_section.eyebrow")}
            </span>
            <h2 className="font-display mt-3 text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight">
              {t("reviews_section.title")}
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              {t("reviews_section.subtitle")}
            </p>
          </div>

          <Carousel
            opts={{ align: "start", loop: true }}
            className="mt-12 px-12"
            data-testid="reviews-carousel"
          >
            <CarouselContent>
              {(Array.isArray(reviews) ? reviews : []).map((rv, i) => {
                const text = lang === "pl" ? (rv.text_pl || rv.text) : (rv.text_en || rv.text);
                return (
                <CarouselItem
                  key={`${rv.id || rv.author}-${i}`}
                  className="md:basis-1/2 lg:basis-1/3"
                  data-testid={`review-card-${i}`}
                >
                  <div className="h-full rounded-2xl border border-slate-200 bg-white p-7 hover:border-sky-300 transition-colors flex flex-col">
                    <Quote className="w-7 h-7 text-sky-200" />
                    <div className="mt-3 flex items-center gap-1 text-amber-500">
                      {Array.from({ length: rv.rating || 5 }).map((_, j) => (
                        <Star key={j} className="w-4 h-4 fill-current" />
                      ))}
                    </div>
                    <p className="mt-4 text-slate-700 leading-relaxed flex-1">
                      "{text}"
                    </p>
                    <div className="mt-5 pt-4 border-t border-slate-100">
                      <div className="font-semibold text-slate-900 text-sm">
                        {rv.author}
                      </div>
                      <div className="text-xs text-slate-500">
                        {rv.location}
                      </div>
                    </div>
                  </div>
                </CarouselItem>
                );
              })}
            </CarouselContent>
            <CarouselPrevious data-testid="reviews-prev" />
            <CarouselNext data-testid="reviews-next" />
          </Carousel>
        </div>
      </section>

      {/* PRICING */}
      <section className="py-24 scroll-mt-20" id="pricing">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-sky-600">
              {t("nav.pricing")}
            </span>
            <h2 className="font-display mt-3 text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight">
              {t("pricing.title")}
            </h2>
            <p className="mt-4 text-slate-600">{t("pricing.subtitle")}</p>
            <div className="mt-6 flex items-center gap-4 text-sm text-slate-600">
              <span className="inline-flex items-center gap-2">
                <Leaf className="w-4 h-4 text-emerald-600" /> {t("pricing.eco")}
              </span>
              <span className="inline-flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-sky-600" /> {t("pricing.local")}
              </span>
            </div>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {services.map((s) => (
              <div
                key={s.id}
                className="rounded-2xl border border-slate-200 bg-white p-7 hover:border-sky-400 transition-colors"
                data-testid={`pricing-card-${s.slug}`}
              >
                <h3 className="font-display text-lg font-semibold">
                  {lang === "pl" ? s.name_pl : s.name_en}
                </h3>
                <div className="mt-3">
                  <span className="font-display text-4xl font-bold text-slate-900">
                    {s.base_price.toFixed(0)}
                  </span>
                  <span className="text-slate-500 ml-1">
                    {t("common.currency")} / {lang === "pl" ? s.unit_pl : s.unit_en}
                  </span>
                </div>
                <ul className="mt-5 space-y-2">
                  {(lang === "pl" ? s.features_pl : s.features_en).map(
                    (f, i) => (
                      <li
                        key={`${s.slug}-feat-${i}`}
                        className="flex items-start gap-2 text-sm text-slate-600"
                      >
                        <CheckCircle2 className="w-4 h-4 text-sky-600 mt-0.5 flex-shrink-0" />
                        {f}
                      </li>
                    ),
                  )}
                </ul>
                <button
                  onClick={() => navigate(`/book?service=${s.slug}`)}
                  className="mt-6 w-full btn-primary"
                  data-testid={`pricing-book-${s.slug}`}
                >
                  {t("nav.book")}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GDANSK / TRUST */}
      <section className="py-20 bg-slate-900 relative overflow-hidden">
        <img
          src={GDANSK_IMG}
          alt="Gdańsk"
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 text-center text-white">
          <span className="text-xs font-bold tracking-[0.2em] uppercase text-sky-300">
            {t("trust.eyebrow")}
          </span>
          <h2 className="font-display mt-3 text-4xl lg:text-5xl font-bold tracking-tight">
            {t("trust.title")}
          </h2>
          <p className="mt-4 text-slate-300 max-w-2xl mx-auto">
            {t("trust.body")}
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 scroll-mt-20" id="faq">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight text-center">
            {t("faq.title")}
          </h2>
          <Accordion type="single" collapsible className="mt-10" data-testid="faq-accordion">
            {t("faq.items").map((item, i) => (
              <AccordionItem
                key={item.q}
                value={`item-${i}`}
                className="border-b border-slate-200"
              >
                <AccordionTrigger
                  className="text-left font-display text-lg font-semibold py-5"
                  data-testid={`faq-q-${i}`}
                >
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pb-5 leading-relaxed">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-sky-600 via-sky-700 to-sky-900 p-10 lg:p-16 text-white">
            <div className="relative max-w-2xl">
              <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight">
                {t("final_cta.title")}
              </h2>
              <p className="mt-4 text-sky-100 text-lg">
                {t("final_cta.subtitle")}
              </p>
              <button
                onClick={() => navigate("/book")}
                className="mt-7 inline-flex items-center gap-2 bg-white text-sky-700 px-6 py-3 rounded-xl font-semibold hover:bg-sky-50 transition"
                data-testid="final-cta-book"
              >
                {t("hero.cta")} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <div className="absolute -right-20 -bottom-20 w-96 h-96 rounded-full bg-sky-400/30 blur-3xl" />
          </div>
        </div>
      </section>
    </div>
  );
}
